/*
 * File: pages/LoginPage.js
 * Mô tả: Component trang đăng nhập với giao diện mới,
 * tích hợp logo công ty và hiệu ứng tilt 3D.
 */
import React, { useState, useRef, useEffect } from 'react';
import { apiLogin } from '../api';


// --- Component Illustration với hiệu ứng Tilt ---
function TiltIllustration() {
    const tiltRef = useRef(null);

    useEffect(() => {
        const element = tiltRef.current;
        if (!element) return;

        const handleMouseMove = (e) => {
            const { left, top, width, height } = element.getBoundingClientRect();
            const x = e.clientX - left;
            const y = e.clientY - top;
            const rotateX = -((y / height) - 0.5) * 25;
            const rotateY = ((x / width) - 0.5) * 25;
            element.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.1, 1.1, 1.1)`;
        };

        const handleMouseLeave = () => {
            element.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
        };
        
        element.addEventListener('mousemove', handleMouseMove);
        element.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            element.removeEventListener('mousemove', handleMouseMove);
            element.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, []);

    return (
        <div ref={tiltRef} style={{ transition: 'transform 0.1s ease' }}>
             <img 
                src="/logo.png" 
                alt="Logo Công ty VINACAPA" 
                className="w-full max-w-4xl" // Tăng kích thước logo
                onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/320x320/cccccc/333333?text=Logo+Error'; }}
            />
        </div>
    );
}


// --- Component LoginForm (Giao diện mới) ---
function LoginForm({ onLogin, error, isLoading }) {
    const [empid, setEmpid] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        onLogin(empid.trim(), password.trim());
    };
    
    const EyeIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7S3.732 16.057 2.458 12z" /></svg>
    );

    const EyeOffIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242" /></svg>
    );

    return (
        <form className="w-full" onSubmit={handleSubmit}>
            <div className="lg:hidden text-center mb-8">
                <img 
                    src="/logo.png" 
                    alt="Logo Công ty VINACAPA" 
                    className="w-full max-w-[150px] mx-auto"
                    onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/150x150/cccccc/333333?text=Logo'; }}
                />
            </div>

            <div className="text-center mb-10">
                 <span className="block text-3xl font-extrabold text-black">
                    Đăng nhập
                </span>
            </div>

            <div className="relative mb-6">
                <input
                    className="block w-full bg-gray-100 border border-gray-300 text-black placeholder-gray-500 px-4 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                    type="text"
                    value={empid}
                    onChange={(e) => setEmpid(e.target.value.toUpperCase())}
                    placeholder="Tên đăng nhập (Mã nhân viên)"
                    required
                />
            </div>

            <div className="relative mb-6">
                <input
                    className="block w-full bg-gray-100 border border-gray-300 text-black placeholder-gray-500 px-4 py-3 pr-10 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mật khẩu (Ngày sinh ddmmyyyy)"
                    required
                />
                <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-black"
                >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
            </div>
            
            {error && <p className="text-sm text-center text-red-600 mb-4">{error}</p>}

            <div className="mt-8">
                <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex justify-center items-center py-3 px-4 rounded-lg shadow-lg text-white font-semibold bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-300 ease-in-out"
                >
                    {isLoading ? 'Đang xử lý...' : 'Đăng nhập'}
                </button>
            </div>
            
            <div className="text-center text-sm text-gray-800 mt-8">
                <p className="mb-3 font-medium">Quên mật khẩu? Liên hệ:</p>
                <div className="flex flex-row justify-center items-center gap-3">
                    <a href="https://zalo.me/0367517649" target="_blank" rel="noopener noreferrer" className="bg-indigo-100 text-indigo-800 font-semibold py-2 px-4 rounded-full hover:bg-indigo-200 transition-colors text-center">
                        Phòng Nhân sự
                    </a>
                </div>
            </div>

            <div className="flex items-center justify-center mt-20 pt-4 border-t border-gray-300">
                <img 
                    src="/logo.png" 
                    alt="Logo Công ty VINACAPA" 
                    className="h-8 w-8 mr-3"
                    onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/32x32/cccccc/333333?text=Logo'; }}
                />
                 <span className="block text-base font-bold text-gray-700">
                    Hệ Thống Nhân Sự VINACAPA
                </span>
            </div>
        </form>
    );
}

function ParticleBackground() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let particlesArray;

        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        class Particle {
            constructor(x, y, directionX, directionY, size) {
                this.x = x; this.y = y;
                this.directionX = directionX; this.directionY = directionY;
                this.size = size;
            }
            draw() {
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2, false);
                ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
                ctx.fill();
            }
            update() {
                if (this.x > canvas.width || this.x < 0) { this.directionX = -this.directionX; }
                if (this.y > canvas.height || this.y < 0) { this.directionY = -this.directionY; }
                this.x += this.directionX;
                this.y += this.directionY;
                this.draw();
            }
        }

        function init() {
            particlesArray = [];
            let numberOfParticles = (canvas.height * canvas.width) / 9000;
            for (let i = 0; i < numberOfParticles; i++) {
                let size = (Math.random() * 2) + 1;
                let x = (Math.random() * ((window.innerWidth - size * 2) - (size * 2)) + size * 2);
                let y = (Math.random() * ((window.innerHeight - size * 2) - (size * 2)) + size * 2);
                let directionX = (Math.random() * 0.4) - 0.2;
                let directionY = (Math.random() * 0.4) - 0.2;
                particlesArray.push(new Particle(x, y, directionX, directionY, size));
            }
        }

        function connect() {
            let opacityValue = 1;
            for (let a = 0; a < particlesArray.length; a++) {
                for (let b = a; b < particlesArray.length; b++) {
                    let distance = ((particlesArray[a].x - particlesArray[b].x) * (particlesArray[a].x - particlesArray[b].x)) +
                                   ((particlesArray[a].y - particlesArray[b].y) * (particlesArray[a].y - particlesArray[b].y));
                    if (distance < (canvas.width / 7) * (canvas.height / 7)) {
                        opacityValue = 1 - (distance / 20000);
                        ctx.strokeStyle = `rgba(255, 255, 255, ${opacityValue})`;
                        ctx.lineWidth = 1;
                        ctx.beginPath();
                        ctx.moveTo(particlesArray[a].x, particlesArray[a].y);
                        ctx.lineTo(particlesArray[b].x, particlesArray[b].y);
                        ctx.stroke();
                    }
                }
            }
        }

        let animationFrameId;
        function animate() {
            animationFrameId = requestAnimationFrame(animate);
            ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
            for (let i = 0; i < particlesArray.length; i++) {
                particlesArray[i].update();
            }
            connect();
        }
        
        const handleResize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            init();
        };
        
        window.addEventListener('resize', handleResize);
        
        init();
        animate();
        
        return () => {
             window.removeEventListener('resize', handleResize);
             cancelAnimationFrame(animationFrameId);
        }

    }, []);

    return <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }} />;
}


// --- Component Trang Đăng Nhập Chính ---
export default function LoginPage({ onLoginSuccess }) {
    const [loginError, setLoginError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleLogin = async (empid, password) => {
        setIsLoading(true);
        setLoginError('');
        try {
            const loggedInUser = await apiLogin(empid, password);
            if(onLoginSuccess) {
                onLoginSuccess(loggedInUser);
            } else {
                alert(`Đăng nhập thành công! Chào mừng ${loggedInUser.name}`);
            }
        } catch (error) {
            setLoginError(error.message || 'Đã có lỗi xảy ra.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="relative min-h-screen font-times bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center p-4 overflow-hidden">
            {/* --- END CHANGE --- */}
                <ParticleBackground />
                {/* --- START CHANGE: Chỉnh lại màu nền khối đăng nhập --- */}
                <div className="relative z-10 w-full max-w-4xl flex flex-col lg:flex-row bg-purple-900/10 backdrop-blur-xl border border-purple-300/20 rounded-2xl shadow-2xl overflow-hidden">
                {/* --- END CHANGE --- */}
                    
                    <div className="w-full lg:w-1/2 hidden lg:flex items-center justify-center p-8 lg:p-12">
                        <TiltIllustration />
                    </div>

                    <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-12">
                        <LoginForm
                            onLogin={handleLogin}
                            error={loginError}
                            isLoading={isLoading}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
